import CheckCircle from "@untitled-ui/icons-react/build/esm/CheckCircle";
import { Fragment as ReactFragment } from "react";
import styled from "styled-components";
import BtnLargeSolid from "../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../common/checkbox/CheckboxVector";
import TooltipWithInfoCircle from "../../../common/tooltip/TooltipWithInfoCircle";

// #region SCOPED CSS
const StyledPlanCard = styled.div`
    background: linear-gradient(180deg, rgba(0, 96, 204, 0.1) 0%, rgba(12, 12, 12, 0.2) 15.76%), #141922;
    border-radius: 20px;
    padding: 26px 32px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
    overflow: hidden;
    box-sizing: border-box;
    min-width: 330px;
    width: 390px;

    &.highlighted {
        box-shadow: 0 0 0 1px var(--text-and-icons-green-500, #00bb00);
    }
    .plan-badge {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: 12px;
        line-height: 21px;
        font-weight: 700;
        text-align: center;
    }
    .plan-header {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }
    .plan-name {
        color: #d1d1d1;
        text-align: center;
        font-size: 26px;
        letter-spacing: -0.015em;
        font-weight: 700;
    }
    .plan-subtitle {
        color: #d1d1d1;
        text-align: center;
        font-size: 14px;
        line-height: 17px;
        font-weight: 400;
        margin: 0 10px;
    }
    .plan-pricing {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }
    .price-display {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: center;
    }
    .price-currency {
        font-size: 19px;
        line-height: 58px;
        font-weight: 700;
    }
    .price-amount {
        font-size: 32px;
        line-height: 58px;
        font-weight: 700;
    }
    .price-period {
        font-size: 19px;
        line-height: 58px;
        font-weight: 700;
    }
    .billing-note {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: center;
        font-size: 14px;
        line-height: 21px;
        font-weight: 400;
    }
    .separator {
        border-top: 1px solid rgba(209, 209, 209, 0.1);
        align-self: stretch;
    }
    .stores-section {
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }
    .stores-title {
        color: #d1d1d1;
        text-align: center;
        font-size: 16px;
        line-height: 25px;
        font-weight: 700;
    }
    .reports-info {
        display: flex;
        flex-direction: row;
        gap: 6px;
        align-items: center;
        justify-content: center;
        padding: 0 10px;
    }
    .info-icon {
        flex-shrink: 0;
        width: 13px;
        height: 13px;
        color: var(--text-icons-gray-200, #7c868b);
        top: -1px;
    }
    .reports-label {
        color: #d1d1d1;
        text-align: center;
        font-size: 14px;
        line-height: 25px;
        font-weight: 400;
    }
    .individual-reports-row {
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
        padding: 0 10px;
    }
    .individual-reports-text {
        color: #d1d1d1;
        font-size: 12px;
        line-height: 25px;
        font-weight: 400;
    }
    .individual-reports-text .nok-label {
        font-size: 10px;
    }
    .feature-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        flex: 1;
    }
    .feature-list.compact {
        flex: 0 0 auto;
    }
    .feature-item {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
    }
    .feature-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        color: var(--Buttons-All-Platforms-Chips, #006d00);
    }
    .feature-text {
        color: #d1d1d1;
        font-size: 14px;
        line-height: 21px;
        font-weight: 400;
        flex: 1;
    }
    .plan-footer {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        margin-top: auto;
    }
    .terms-row {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        align-self: stretch;
    }
    .terms-text {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 12px;
        font-weight: 400;
        flex: 1;
    }
    .terms-link {
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        text-decoration: underline;
        cursor: pointer;
    }
    .terms-link:disabled {
        cursor: not-allowed;
    }
`;
// #endregion SCOPED CSS

// #region CONSTANTS
const NOOP = () => {};
// #endregion CONSTANTS

/** Renders the shared plan-card structure from UI-ready plan data and interaction props. */
export default function BillingPlanCardBuilder({
    plan,
    ctaLabel,
    isTermsAccepted = false,
    isTermsDisabled = false,
    isActionDisabled = true,
    onTermsChange = NOOP,
    onTermsClick = NOOP,
    onPrivacyClick = NOOP,
    onActionClick = NOOP
}) {
    return (
        <StyledPlanCard className={plan.isHighlighted ? "highlighted" : undefined}>
            <div className="plan-badge">{plan.badge}</div>

            <SectionPlanHeader title={plan.title} description={plan.description} />

            <SectionPlanPricing {...plan.price} />

            <div className="separator" />

            <SectionPlanAllowance {...plan.allowance} />

            {plan.sections.map(section => (
                <ReactFragment key={section.id}>
                    <div className="separator" />

                    <SectionPlanCard section={section} />
                </ReactFragment>
            ))}

            <div className="separator" />

            <FooterPlan
                ctaLabel={ctaLabel || plan.ctaLabel}
                isTermsAccepted={isTermsAccepted}
                isTermsDisabled={isTermsDisabled}
                isActionDisabled={isActionDisabled}
                onTermsChange={onTermsChange}
                onTermsClick={onTermsClick}
                onPrivacyClick={onPrivacyClick}
                onActionClick={onActionClick}
            />
        </StyledPlanCard>
    );
}

// #region SMALL COMPONENTS
function SectionPlanHeader({ title, description }) {
    return (
        <div className="plan-header">
            <div className="plan-name">{title}</div>
            <div className="plan-subtitle">{description}</div>
        </div>
    );
}

function SectionPlanPricing({ currency, amount, period, billingNote }) {
    return (
        <div className="plan-pricing">
            <div className="price-display">
                <span className="price-currency">{currency} </span>
                <span className="price-amount">{amount}</span>
                <span className="price-period">{period}</span>
            </div>
            <div className="billing-note">{billingNote}</div>
        </div>
    );
}

function SectionPlanAllowance({ title, label, tooltip }) {
    return (
        <div className="stores-section">
            <div className="stores-title">{title}</div>
            <div className="reports-info">
                <TooltipWithInfoCircle
                    iconClassName="info-icon"
                    header={tooltip.header}
                    title={tooltip.title}
                    iconSize="13px"
                />
                <div className="reports-label">{label}</div>
            </div>
        </div>
    );
}

function SectionPlanCard({ section }) {
    switch (section.type) {
        case "individualReport":
            return <SectionIndividualReport {...section} />;
        case "features":
            return <SectionFeatures {...section} />;
        default:
            return null;
    }
}

function SectionIndividualReport({ prefix, price, currency, tooltip }) {
    return (
        <div className="individual-reports-row">
            <TooltipWithInfoCircle
                iconClassName="info-icon"
                header={tooltip.header}
                title={tooltip.title}
                iconSize="13px"
            />
            <div className="individual-reports-text">
                {prefix} <span className="nok-label">{currency}</span> {price} each
            </div>
        </div>
    );
}

function SectionFeatures({ items, fillAvailableSpace = false }) {
    return (
        <div className={`feature-list ${fillAvailableSpace ? "" : "compact"}`}>
            {items.map(item => (
                <div className="feature-item" key={item}>
                    <CheckCircle className="feature-icon" />
                    <div className="feature-text">{item}</div>
                </div>
            ))}
        </div>
    );
}

function FooterPlan({
    ctaLabel,
    isTermsAccepted,
    isTermsDisabled,
    isActionDisabled,
    onTermsChange,
    onTermsClick,
    onPrivacyClick,
    onActionClick
}) {
    return (
        <div className="plan-footer">
            <div className="terms-row">
                <CheckboxVector
                    className="checkbox"
                    isChecked={isTermsAccepted}
                    disabled={isTermsDisabled}
                    onCheckboxClick={onTermsChange}
                />
                <div className="terms-text">
                    I agree to the{" "}
                    <button className="terms-link" type="button" onClick={onTermsClick}>
                        Terms and Conditions
                    </button>{" "}
                    and{" "}
                    <button className="terms-link" type="button" onClick={onPrivacyClick}>
                        Privacy Policy
                    </button>
                    .
                </div>
            </div>

            <BtnLargeSolid
                className="add-color-button"
                text={ctaLabel}
                disabled={isActionDisabled}
                onClick={onActionClick}
                normalColor="var(--Buttons-All-Platforms-Chips, #006D00)"
                textStyle={{
                    fontSize: "12px",
                    textTransform: "uppercase"
                }}
                style={{ width: "214px" }}
            />
        </div>
    );
}
// #endregion SMALL COMPONENTS
